import {
  startFlightCheckout,
  updateDepartingFlightSegmentCost,
  updateJourneyCost,
  updateReturningFlightSegmentCost,
  updateTotalCostWithAtolFee,
  updateTotalCostWithBookingFee,
  updateTotalCostWithFlightsCreditFee,
  updateTotalCostWithServiceFee,
  updateTwoWayPassengerBaggages,
} from 'actions/CartActions'
import { AppApiAction } from './ActionTypes'
import {
  API_CALL,
  CLEAR_FLIGHT_RESULT,
  FETCH_FLIGHT_CREDIT_RESERVATION,
  FETCH_FLIGHT_DEAL,
  FETCH_FLIGHT_DEALS,
  FETCH_POPULAR_AIRPORTS,
  SET_FLIGHTS_CREDIT_DATA,
  UPDATE_FLIGHT_FILTERS,
  UPDATE_FLIGHTS_SORT,
} from './actionConstants'
import {
  getFlightBaggage,
  getFlightCreditReservation,
  getFlightDealsByRegion,
  getFlightPrice,
  getJourney,
  getJourneyV2,
  getPopularAirportsByRegion,
  searchFlights,
  selectflights,
  getFlightCachePrices,
  FlightSelectParams,
  getFlightFareFamilies,
  getFlightDeal,
} from 'api/flights'
import { getDefaultAirportCode } from 'selectors/flightsSelectors'
import { resetFilter } from 'lib/flights/flightUtils'
import {
  FETCH_FLIGHT_BAGGAGE,
  FETCH_FLIGHT_JOURNEY,
  FETCH_FLIGHT_PRICE,
  FETCH_FLIGHT_CALENDAR,
  FETCH_FLIGHTS,
  FETCH_SEARCH_FLIGHTS,
  GET_FINAL_FLIGHT_FARES,
  FETCH_FLIGHT_FARE_FAMILIES,
} from './apiActionConstants'
import { getFlightPriceKey, getFlightSearchKey } from 'lib/flights/flightUtils'
import { sum } from 'lib/array/arrayUtils'
import { FlightSortOptions } from 'constants/flight'
import { FlightsCreditDetails } from 'components/Flights/types'
import { expireFlightsTimer, resetFlightsTimer } from 'components/Flights/FlightsSessionTimer/FlightsSessionTimer'
import { pushWithRegion } from './NavigationActions'
import getObjectKey from 'lib/object/getObjectKey'
import { cartIncludesFlights, getFlightItems } from 'checkout/selectors/view/flights'
import { isFlightsPendingToAddToCart } from 'checkout/selectors/navigationSelectors'

interface GetFlightsParams {
  startDate: string;
  endDate: string;
  origin: string;
  destination: string;
  currency: string;
  occupancies: Array<App.Occupants>;
  fareClass: string;
  maxArrivalTime?: string;
  minReturningDepartureTime?: string;
  carriers?: Array<string>;
  fareType?: string;
  source?: string;
  searchIndex?: number;
  viewType?: string;
  forceBundleId?: string;
}

export function getFlights(params: GetFlightsParams) {
  return (dispatch, getState) => {
    const state = getState() as App.State
    const key = getFlightSearchKey(params)

    if (state.flights.fetchedKey === key) {
      // we've already fetched this key (or are fetching it currently)
      return
    }

    resetFlightsTimer()

    dispatch({
      type: API_CALL,
      api: FETCH_FLIGHTS,
      request: () => searchFlights({
        ...params,
        region: state.geo.currentRegionCode,
      }).then(result => {
        dispatch(startFlightCheckout())
        return result
      }),
      key,
    })
  }
}

export function fetchFlightJourneyV1(searchId: string) {
  return (dispatch, getState) => {
    const state = getState() as App.State

    if (
      state.flights.journeysById[searchId] ||
      state.flights.journeyFetching[searchId]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_FLIGHT_JOURNEY,
      key: searchId,
      request: () => getJourney(searchId, state.auth.accessToken),
    })
  }
}

export function fetchFlightJourneyV2(
  searchId: string,
  journeyId: string,
  fareFamilyId?: string,
) {
  return (dispatch, getState) => {
    const state = getState() as App.State

    const journeyKey = getObjectKey({
      journeyId,
      searchId,
      fareFamilyId,
    })

    if (state.flights.journeysById[journeyKey] || state.flights.journeyFetching[journeyKey]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_FLIGHT_JOURNEY,
      key: journeyKey,
      request: () => getJourneyV2(searchId, journeyId, state.auth.accessToken, fareFamilyId),
    })
  }
}

export function fetchFlights(params: GetFlightsParams) {
  return (dispatch, getState) => {
    const state = getState() as App.State
    const key = getFlightSearchKey(params)

    // Reset flights timer for each fetch unless flights exist in the cart
    if (!cartIncludesFlights(state) || (getFlightItems(state).length == 1 && isFlightsPendingToAddToCart(state))) {
      resetFlightsTimer()
    }

    if (state.flights.searchFlights[key] || state.flights.searchV2Flights[key]) {
      // already fetched or is fetching, don't fetch again
      return
    }

    // TODO: Look into adding an expiry time for the results
    // so they don't stick around forever and go out of date

    dispatch({
      type: API_CALL,
      api: FETCH_SEARCH_FLIGHTS,
      request: () => searchFlights({
        ...params,
        region: state.geo.currentRegionCode,
      }),
      key,
    })
  }
}

export function getFlightSelectedPrice({
  journeyId,
  departingJourneyKey,
  returningJourneyKey,
  departingAirport,
  arrivalAirport,
  providerSearchId,
  provider,
  isFlightBaggageAvailable,
  isStandalone,
  isFlightsCredit,
  viewType,
  selectedFareIds,
  forceBundleId,
}: FlightSelectParams) {
  return (dispatch, getState) => {
    const { geo } = getState() as App.State
    let callCount = 0
    const getFlightSelect = new Promise<void>((resolve, reject) => {
      const getSelectedFlightInfo = () => {
        callCount++
        selectflights({
          journeyId,
          departingJourneyKey,
          returningJourneyKey,
          departingAirport,
          arrivalAirport,
          providerSearchId,
          region: geo.currentRegionCode,
          isStandalone,
          provider,
          isFlightsCredit,
          viewType,
          selectedFareIds,
          forceBundleId,
        })
          .then(response => {
            const { status, result: { CommonPriceDetails, selectedFares } } = response

            if (status === 408) {
              // Flights API timer expired
              expireFlightsTimer()
            }

            const totalFeeObject = CommonPriceDetails.find(
              ({ passengerTypeQuantity }) =>
                passengerTypeQuantity.code === 'ALL',
            )
            const totalFare = totalFeeObject?.totalFareOriginalCurrency
            const bookingFeeFromAdminData = CommonPriceDetails.find(
              ({ passengerTypeQuantity }) =>
                passengerTypeQuantity.code === 'booking_fee',
            )?.totalFare

            const flightsCreditFeeFromAdminData = CommonPriceDetails.find(
              ({ passengerTypeQuantity }) =>
                passengerTypeQuantity.code === 'flights_credit_fee',
            )?.totalFare

            const atolFeeFromAdminData = CommonPriceDetails.find(
              ({ passengerTypeQuantity }) =>
                passengerTypeQuantity.code === 'atol_fee',
            )?.totalFare

            const serviceFeeFromAdminData = CommonPriceDetails.find(
              ({ passengerTypeQuantity }) =>
                passengerTypeQuantity.code === 'service_fee',
            )?.totalFare

            const bookingFee = bookingFeeFromAdminData && parseFloat(bookingFeeFromAdminData) > 0 ? parseFloat(bookingFeeFromAdminData) : 0
            const atolFee = atolFeeFromAdminData && parseFloat(atolFeeFromAdminData) > 0 ? parseFloat(atolFeeFromAdminData) : 0
            const flightsCreditFee = flightsCreditFeeFromAdminData && parseFloat(flightsCreditFeeFromAdminData) > 0 ? parseFloat(flightsCreditFeeFromAdminData) : 0
            const serviceFee = serviceFeeFromAdminData && parseFloat(serviceFeeFromAdminData) > 0 ? parseFloat(serviceFeeFromAdminData) : 0

            if (bookingFee > 0 && !isFlightsCredit) {
              dispatch(updateTotalCostWithBookingFee(bookingFee))
            }

            if (atolFee > 0) {
              dispatch(updateTotalCostWithAtolFee(atolFee))
            }

            if (isFlightsCredit && flightsCreditFee > 0) {
              dispatch(updateTotalCostWithFlightsCreditFee(flightsCreditFee))
            }

            if (serviceFee > 0) {
              dispatch(updateTotalCostWithServiceFee(serviceFee))
            }

            if (selectedFares && selectedFares.length === 2) {
              // twoOneWay flights
              const updatedCosts = selectedFares.map(fare => Number(fare.price.all?.totalFareOriginalCurrency))
              dispatch(updateDepartingFlightSegmentCost(updatedCosts[0]))
              dispatch(updateReturningFlightSegmentCost(updatedCosts[1]))
            }

            if (totalFeeObject) {
              const totalCost = totalFare + bookingFee + atolFee + serviceFee
              dispatch(updateJourneyCost(totalCost))
              const baggage = selectedFares
                .map(selectedFare => {
                  const baggageOption = selectedFare.flightGroups[0].flights[0].checkedBaggageOptions.find(
                    ({ amount }) => !amount,
                  )
                  if (baggageOption) {
                    return {
                      id: `${baggageOption.providerKey}~${baggageOption.quantity}~${baggageOption.amount}~${baggageOption.carrierCode}`,
                      count: 1,
                    }
                  }
                })
                .filter(Boolean)
              if (baggage.length) {
                dispatch(updateTwoWayPassengerBaggages(journeyId, baggage))
              }
              const {
                checkedBaggageOptions,
              } = selectedFares[0].flightGroups[0].flights[0]
              if (!checkedBaggageOptions.length && !isFlightBaggageAvailable) {
                dispatch(getBaggage(journeyId, provider))
              }
            }

            if (isStandalone) {
              dispatch(pushWithRegion('/booking/payment'))
            }
            resolve()
          })
          .catch(() => {
            if (callCount > 2) {
              reject()
            } else {
              getSelectedFlightInfo()
            }
          })
      }
      getSelectedFlightInfo()
    })

    dispatch({
      type: API_CALL,
      api: GET_FINAL_FLIGHT_FARES,
      request: () => getFlightSelect,
    })
  }
}

export function getBaggage(
  journeyId: string,
  provider: string,
  flightSearchKey?: string,
) {
  return (dispatch, getState) => {
    const { geo } = getState() as App.State
    dispatch({
      type: API_CALL,
      api: FETCH_FLIGHT_BAGGAGE,
      request: () => getFlightBaggage({
        journeyId,
        provider,
        region: geo.currentRegionCode,
      }).then(data => ({
        journeyId,
        flightSearchKey,
        newBaggage: [{
          baggage: data.baggage[0],
          carryOnBaggage: data.carryOnBaggage[0],
          baggageNote: data.baggageNote,
          isBagsSameForRoundTrip: data.isBagsSameForRoundTrip,
        },
        {
          baggage: data.baggage[1],
          carryOnBaggage: data.carryOnBaggage[1],
          baggageNote: data.baggageNote,
          isBagsSameForRoundTrip: data.isBagsSameForRoundTrip,
        }],
      })),
    })
  }
}

export function updateFlightFilters<T extends App.AnyJourney = App.Journey>(filters: App.FlightFilter<T>[]) {
  return {
    type: UPDATE_FLIGHT_FILTERS,
    data: filters,
  }
}

export function updateFlightFilter(filter: App.FlightFilter) {
  return (dispatch, getState) => {
    const state = getState() as App.State

    const newFilters = [
      ...state.flights.filters.filter(f => f.id !== filter.id),
      filter,
    ]

    dispatch({
      type: UPDATE_FLIGHT_FILTERS,
      data: newFilters,
    })
  }
}

export function updateFlightsSort(sortValue: FlightSortOptions) {
  return {
    type: UPDATE_FLIGHTS_SORT,
    data: sortValue,
  }
}

export function clearFlightsResult() {
  return {
    type: CLEAR_FLIGHT_RESULT,
  }
}

export function resetFlightsFilters() {
  return (dispatch, getState) => {
    const state = getState() as App.State

    const newFilters = state.flights.filters.map(f => resetFilter(f))

    dispatch({
      type: UPDATE_FLIGHT_FILTERS,
      data: newFilters,
    })
  }
}

export function fetchFlightPrice(
  destinationAirportCode: string,
  rooms: Array<App.Occupants>,
  startDate: string,
  endDate: string,
  flightOrigin?: string,
  nights?: number,
) {
  return (dispatch, getState) => {
    const state = getState() as App.State

    const finalOrigin = flightOrigin || getDefaultAirportCode(state)

    const requestProps = {
      startDate,
      endDate,
      numberOfAdults: sum(rooms, (room) => room.adults),
      numberOfChildren: sum(rooms, (room) => room.children ?? 0),
      numberOfInfants: sum(rooms, (room) => room.infants ?? 0),
      origin: finalOrigin,
      destination: destinationAirportCode,
      nights,
    }

    const currency = state.geo.currentCurrency
    const region = state.geo.currentRegionCode
    const key = getFlightPriceKey(requestProps)

    if (
      // can be 0, which is still falsey - but we don't want to refetch it
      typeof state.flights.flightPrices[key] !== 'undefined' ||
      state.flights.flightPricesLoading[key]
    ) {
      // fetching or already fetched, skip
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_FLIGHT_PRICE,
      request: () => getFlightPrice({ ...requestProps, currency, region }),
      key,
    })
  }
}

export function fetchFlightPrices(
  destinationAirportsCodes: Array<string>,
  rooms: Array<App.Occupants>,
  startDate: string,
  endDate: string,
  flightOrigin?: string,
  nights?: number,
) {
  return (dispatch, getState) => {
    const state = getState() as App.State

    const finalOrigin = flightOrigin || getDefaultAirportCode(state)

    const requestProps = {
      startDate,
      endDate,
      numberOfAdults: sum(rooms, (room) => room.adults),
      numberOfChildren: sum(rooms, (room) => room.children ?? 0),
      numberOfInfants: sum(rooms, (room) => room.infants ?? 0),
      origin: finalOrigin,
      nights,
    }

    const currency = state.geo.currentCurrency
    const region = state.geo.currentRegionCode
    // dedupe airports first
    const missingDestination = Array.from(new Set(destinationAirportsCodes.filter(Boolean)))
      // now get our keys
      .map(destination => ({ key: getFlightPriceKey({ ...requestProps, destination }), airportCode: destination }))
      // finally find those that aren't already fetched/fetching
      .filter(destination =>
        // can be 0, which is still falsey - but we don't want to refetch it
        typeof state.flights.flightPrices[destination.key] === 'undefined' &&
        !state.flights.flightPricesLoading[destination.key],
      )

    missingDestination.forEach(destination => {
      dispatch({
        type: API_CALL,
        api: FETCH_FLIGHT_PRICE,
        request: () => getFlightPrice({ ...requestProps, destination: destination.airportCode, currency, region, nights }),
        key: destination.key,
      })
    })
  }
}

export function getPopularAirports() {
  return (dispatch, getState) => {
    const state = getState() as App.State

    if (state.flights.popularAirports.origins?.length) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_POPULAR_AIRPORTS,
      request: () => getPopularAirportsByRegion(state.geo.currentRegionCode),
    })
  }
}

export function setFlightCreditData(creditDetails: FlightsCreditDetails) {
  return {
    type: SET_FLIGHTS_CREDIT_DATA,
    data: creditDetails,
  }
}

export function fetchFlightsCreditReservation(creditReservationId: string) {
  return {
    type: API_CALL,
    api: FETCH_FLIGHT_CREDIT_RESERVATION,
    request: () => getFlightCreditReservation(creditReservationId),
  }
}

export function fetchFlightDeals(dealCategory: string = 'all') {
  return (dispatch, getState) => {
    const state = getState() as App.State

    const finalCategory = dealCategory === 'all' ? undefined : dealCategory

    const existingList = state.flights.flightDealLists[dealCategory]
    if (existingList) {
      // if a list exists, it must've been initialised and is currently fetching/has fetched or is errored
      // we don't need to do it again
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_FLIGHT_DEALS,
      request: () => getFlightDealsByRegion(state.geo.currentRegionCode, finalCategory),
      // only thing we key on at the moment is deal category
      key: dealCategory,
    })
  }
}

export function fetchFlightDeal(id: string, token?: string) {
  return (dispatch, getState) => {
    const state = getState() as App.State

    if (state.flights.flightDeals[id]) {
      // already done (or doing), don't do it again
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_FLIGHT_DEAL,
      request: () => getFlightDeal(id, token),
      id,
    })
  }
}

interface FlightCalendarProps {
  startDate: string;
  endDate: string;
  origin: string;
  destination: string;
  nights?: number;
}

export function getFlightCalendarKey(props: FlightCalendarProps & { region: string; currency: string; }) {
  return `${props.startDate}-${props.endDate}-${props.origin}-${props.destination}-${props.nights}-${props.region}-${props.currency}`
}

export function fetchFlightCalendar(props: FlightCalendarProps): AppApiAction {
  return (dispatch, getState) => {
    const state = getState()
    const region = state.geo.currentRegionCode
    const currency = state.geo.currentCurrency

    const filterKey = getFlightCalendarKey({ ...props, region, currency })
    if (state.flights.flightCalendar[filterKey] || state.flights.flightCalendarLoading[filterKey]) {
      // already have fetched the calendar for the current filters
      return
    }

    const requestData = {
      startDate: props.startDate,
      endDate: props.endDate,
      origin: props.origin,
      destination: props.destination,
      nights: props.nights,
      region,
      currency,
    }

    dispatch({
      type: API_CALL,
      api: FETCH_FLIGHT_CALENDAR,
      request: () => getFlightCachePrices(requestData),
      key: filterKey,
    })
  }
}
interface FetchFlightUpsellFaresParams {
  searchId: string;
  sessionId: string;
  origin: string;
  destination: string;
  occupancies: App.Occupants;
  journeyId: string;
  departingJourneyId?: string;
  departingFareFamilyId?: string;
}

export function fetchFlightFareFamilies(params: FetchFlightUpsellFaresParams) {
  return (dispatch, getState) => {
    const state = getState() as App.State
    const requestProps = {
      ...params,
      departingFareId: params.departingJourneyId,
      departingUpsellOptionId: params.departingFareFamilyId,
      journeyId: params.journeyId,
      region: state.geo.currentRegionCode,
    }

    const key = getObjectKey({
      searchId: params.searchId,
      origin: params.origin,
      destination: params.destination,
      fareId: params.journeyId,
      departingUpsellOptionId: params.departingFareFamilyId,
    })

    if (state.flights.fareFamiliesByFlight[key]) {
      // already fetching or have already fetched
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_FLIGHT_FARE_FAMILIES,
      request: () => getFlightFareFamilies(requestProps).then(fareFamilies => {
        if (fareFamilies.length === 0) {
          throw new Error('No fare families found for flight')
        }
        return fareFamilies
      }),
      key,
    })
  }
}
